import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import './MobileCalendarStability.css';

// One observer serves the entire calendar, including nested scrolling areas.
let observer = null;
const subscribers = new Map();
function subscribe(node, update) {
  if (!observer) observer = new IntersectionObserver(entries => {
    entries.forEach(entry => subscribers.get(entry.target)?.(entry.isIntersecting));
  }, { rootMargin: '800px 0px', threshold: 0 });
  subscribers.set(node, update);
  observer.observe(node);
  return () => {
    observer.unobserve(node);
    subscribers.delete(node);
    if (!subscribers.size) { observer.disconnect(); observer = null; }
  };
}

const viewportSubscribers = new Set();
const notifyViewport = () => viewportSubscribers.forEach(update => update());
const subscribeViewport = update => {
  if (!viewportSubscribers.size) window.addEventListener('resize', notifyViewport);
  viewportSubscribers.add(update);
  return () => {
    viewportSubscribers.delete(update);
    if (!viewportSubscribers.size) window.removeEventListener('resize', notifyViewport);
  };
};
const isMobileViewport = () => window.innerWidth <= 768;

export default function DeferredCalendarDay({ dayId, renderDay }) {
  const mobile = useSyncExternalStore(subscribeViewport, isMobileViewport);
  const defer = typeof IntersectionObserver !== 'undefined' && mobile;
  const [visible, setVisible] = useState(!defer);
  const visibility = useRef(!defer);
  const shell = useRef(null);
  const height = useRef(280);
  useEffect(() => {
    if (!defer) { visibility.current = true; setVisible(true); return undefined; }
    return subscribe(shell.current, next => {
      // Only measure a mounted day. Repeated observer notifications must not
      // measure the placeholder or trigger another layout update.
      if (next === visibility.current) return;
      if (!next && shell.current) height.current = Math.max(height.current, shell.current.getBoundingClientRect().height);
      visibility.current = next;
      setVisible(next);
    });
  }, [defer]);
  return <div ref={shell} className="calendar-day-shell" data-calendar-day-id={dayId}
    style={defer ? { minHeight: `${height.current}px`, overflowAnchor: 'none' } : undefined}>
    {visible ? renderDay() : <div data-day-id={dayId} className="calendar-day-placeholder" style={{ height: `${height.current}px` }} aria-hidden="true" />}
  </div>;
}
